
import React, { useState, useEffect, useContext } from "react";
import { jwtDecode } from "jwt-decode";
import Backdrop from "@mui/material/Backdrop";
import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";
import ReCAPTCHA from "react-google-recaptcha";
import LogoutIcon from "@mui/icons-material/Logout";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

import { AuthContext } from "context"; // Import AuthContext
import AuthService from "services/auth-service";
import { useNavigate } from "react-router-dom"; // Import useNavigate

const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

const VerifyEmailModal = ({ open, onClose }) => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("info"); // "success" or "error"
  const [captchaToken, setCaptchaToken] = useState(null);

  const authContext = useContext(AuthContext);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      try {
        const decoded = jwtDecode(token);
        setEmail(decoded.email);
      } catch (error) {
        console.error("Error decoding token:", error);
      }
    }
  }, []);

  const handleResendVerification = async () => {
    setMessage("");
    setMessageType("info");

    if (!captchaToken) {
      setMessage("Please verify you are not a robot.");
      setMessageType("error");
      return;
    }

    setLoading(true);

    try {
      const payload = {
        data: {
          type: "Resend-verification",
          attributes: {
            email: email,
            recaptchaToken: captchaToken,
          },
        },
      };

      await AuthService.resendVerificationLink(payload);
      setMessage("A new verification link has been sent to your email.");
      setMessageType("success");
      setCaptchaToken(null);
    } catch (error) {
      setMessage("Failed to resend verification email. Please try again.");
      setMessageType("error");
      console.error("Error resending verification link:", error);
    }

    setLoading(false);
  };

  const handleLogOut = async () => {
    await AuthService.logout();
    authContext.logout();
    navigate("/authentication/sign-in");
  };

  return (
    <Backdrop
      open={open}
      sx={{
        zIndex: (theme) => theme.zIndex.modal + 1,
        backgroundColor: "rgba(0, 0, 0, 0.8)",
      }}
    >
      {/* 🔴 FIXED LOGOUT BUTTON (Visible over backdrop) */}
      <MDBox
        sx={{
          position: "fixed",
          top: "20px",
          right: "30px",
          zIndex: 1500, // Higher than modal + 2
        }}
      >
        <MDButton
          variant="contained"
          color="accent"
          onClick={handleLogOut}
          startIcon={<LogoutIcon />}
        >
          Log Out
        </MDButton>
      </MDBox>
      <Card
        sx={{
          width: "90%",
          maxWidth: 500,
          p: 3,
          backgroundColor: "white",
          borderRadius: 2,
          textAlign: "center",
        }}
      >
        <MDBox
          sx={{
            backgroundColor: "#FFF3E0",
            padding: 2,
            borderRadius: 1,
            mb: 2,
          }}
        >
          <MDTypography variant="body2" color="textPrimary">
            ⚠️ Your email is not verified. Please check your inbox.
          </MDTypography>
        </MDBox>

        <MDTypography variant="h6" fontWeight="bold" gutterBottom>
          Verify Your Email
        </MDTypography>
        <MDTypography variant="body1" color="textPrimary">
          <strong>{email || "N/A"}</strong>
        </MDTypography>

        {message && (
          <MDTypography variant="body2" color={messageType} mt={2}>
            {message}
          </MDTypography>
        )}

        {/* reCAPTCHA widget */}
        <MDBox display="flex" justifyContent="center" alignItems="center" mt={2}>
          <ReCAPTCHA
            sitekey={RECAPTCHA_SITE_KEY}
            onChange={(token) => setCaptchaToken(token)}
          />
        </MDBox>


        <MDBox mt={2}>
          <MDButton
            variant="contained"
            color="accent"
            onClick={handleResendVerification}
            disabled={loading}
            fullWidth
          >
            {loading ? <CircularProgress size={20} color="accent" /> : "Resend Verification Link"}
          </MDButton>
        </MDBox>
      </Card>
    </Backdrop>
  );
};

export default VerifyEmailModal;

